<docs>
The Zone action button: a `<button>`, or an `<a>` when `href` is set. The label is the default slot.
No events of its own: your `@click` lands on the real `<button>`/`<a>` as a native listener and is
swallowed while disabled. ZoneIconButton wraps this component for an icon-only button (a single
`icon` prop instead of slots, round by default, and the spec's XS/S sizes of 16px/20px that this
scale does not reach); ZoneButtonDropdown for a button that opens a menu of actions.

## Notes

- `inheritAttrs: false`: extra attrs (class included) are merged onto the root element via
  `rootAttrs`; your class is appended, not replaced.
- `disabled` uses `aria-disabled`, so the element stays natively clickable; `rootAttrs` swaps your
  onClick for one that only prevents the default and stops propagation. Test intent via
  `aria-disabled` or an onClick spy that was not called. A disabled button is its normal self at
  reduced opacity (30% solid/ghost, 50% outline), no separate palette; hover is suppressed rather
  than restyled.
- `iconOnly` swaps the size classes for a square set (h-8/w-8, h-10/w-10, h-12/w-12) with `px-0`, so
  the icon is optically centred at every size/variant/color. `variant` stays free for
  solid|outline|ghost: the design system pairs icon-only with all three, so icon-only is deliberately
  not a variant value.
- Auto-detection walks the default slot's vnodes: a false `v-if` (a Comment), an empty `v-for` (a
  childless Fragment) and whitespace-only text all count as "no label", so a button whose label is
  conditionally hidden still squares up. Pass `:icon-only="true|false"` to skip the walk.
- Icon size follows the button size, per Figma: `sm`/`md` render an 18px icon (ZoneIcon `lg`), `lg` a
  24px one (ZoneIcon `xxl`). Pass markup via `#leading-icon` / `#trailing-icon` for another size.
- Focus is drawn entirely with box-shadows (a 2px primary-700 ring outside and a 1.5px white hairline
  inside), so focusing never reflows the layout. Ghost keeps its hover tint while focused.
- `rounded` is only in the spec for icon buttons, but it is not gated on `iconOnly`: a silent no-op is
  worse than a pill-shaped text button you can see and undo.
- The `#indicator` badge hangs off a fixed 8px anchor box at the corner, right-aligned and vertically
  centred in it, so a dot sits on the corner and a longer count ("99+") grows leftwards from the same
  point. The anchor tucks in 2px on a round button, which has no corner for it to straddle.
</docs>

<template>
  <!-- ZoneTooltip is renderless without content, so a button with no tooltip is not wrapped. -->
  <ZoneTooltip :content="tooltip">
    <component
      :is="tag"
      v-bind="rootAttrs"
    >
      <slot name="leading-icon">
        <ZoneIcon
          v-if="leadingIcon"
          :icon="leadingIcon"
          :size="iconSize"
        />
      </slot>

      <slot />

      <slot name="trailing-icon">
        <ZoneIcon
          v-if="trailingIcon"
          :icon="trailingIcon"
          :size="iconSize"
        />
      </slot>

      <div
        v-if="$slots.indicator"
        :class="indicatorWrapperClasses"
      >
        <slot name="indicator" />
      </div>
    </component>
  </ZoneTooltip>
</template>

<script lang="ts">
import { Comment, Fragment, Text, type VNode, computed, useAttrs, type PropType } from 'vue';
import ZoneIcon, { type IconName } from './Icon.vue';
import ZoneTooltip from './Tooltip.vue';

const VARIANTS = ['solid', 'outline', 'ghost'] as const;

export type ButtonVariant = (typeof VARIANTS)[number];

const COLORS = ['primary', 'primary_alt', 'red', 'green', 'yellow', 'purple', 'gray', 'gray_alt', 'white'] as const;

export type ButtonColor = (typeof COLORS)[number];

const SIZES = ['sm', 'md', 'lg'] as const;

export type ButtonSize = (typeof SIZES)[number];

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'text-sm h-8 px-3',
  md: 'text-sm h-10 px-5',
  lg: 'text-md h-12 px-6',
};

// Icon-only buttons are perfect squares: the width matches SIZE_CLASSES' height and the
// horizontal padding goes away. These REPLACE the SIZE_CLASSES entry rather than being appended
// to it, so there's never a `px-3` vs `px-0` pair whose winner depends on Tailwind's output order.
// The Zone spec's XS/S icon buttons (16px/20px) are smaller than any ZoneButton height, so
// sm/md/lg here map to the spec's M/L/XL squares.
const ICON_ONLY_SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'text-sm h-8 w-8 px-0',
  md: 'text-sm h-10 w-10 px-0',
  lg: 'text-md h-12 w-12 px-0',
};

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  solid: 'border aria-disabled:opacity-30',
  outline:
    'border aria-disabled:hover:bg-transparent focus-visible:bg-transparent focus-visible:border-transparent aria-disabled:opacity-50',
  ghost: 'bg-transparent aria-disabled:hover:bg-transparent aria-disabled:opacity-30',
};

// Keyed as `${variant}:${color}` — ported from Zone::Button's `compound` style declarations.
const COMPOUND_CLASSES: Record<string, string> = {
  'solid:primary':
    'text-white bg-primary-500 hover:bg-primary-600 focus-visible:bg-primary-500 aria-disabled:hover:bg-primary-500 aria-disabled:hover:border-primary-500 border-primary-500 hover:border-primary-600',
  'outline:primary':
    'text-primary-500 hover:text-primary-500 hover:bg-primary-100 aria-disabled:hover:text-primary-500 aria-disabled:hover:border-primary-500 border-primary-500 hover:border-primary-500',
  'ghost:primary': 'text-primary-500 hover:text-primary-500 hover:bg-primary-100 focus-visible:bg-primary-100 aria-disabled:hover:text-primary-500',

  'solid:primary_alt':
    'text-white bg-primary-700 hover:bg-primary-800 focus-visible:bg-primary-700 aria-disabled:hover:bg-primary-700 aria-disabled:hover:border-primary-700 border-primary-700 hover:border-primary-800',
  'outline:primary_alt':
    'text-primary-700 hover:text-primary-700 hover:bg-primary-100 aria-disabled:hover:text-primary-700 aria-disabled:hover:border-primary-700 border-primary-700 hover:border-primary-700',
  'ghost:primary_alt': 'text-primary-700 hover:text-primary-700 hover:bg-primary-100 focus-visible:bg-primary-100 aria-disabled:hover:text-primary-700',

  'solid:red':
    'text-white bg-red-700 hover:bg-red-800 focus-visible:bg-red-700 aria-disabled:hover:bg-red-700 aria-disabled:hover:border-red-700 border-red-700 hover:border-red-800',
  'outline:red': 'text-red-700 hover:bg-red-100 border-red-700',
  'ghost:red': 'text-red-700 hover:bg-red-100 focus-visible:bg-red-100',

  'solid:green':
    'text-white bg-green-500 hover:bg-green-600 focus-visible:bg-green-500 aria-disabled:hover:bg-green-500 aria-disabled:hover:border-green-500 border-green-500 hover:border-green-600',
  'outline:green': 'text-green-600 hover:bg-green-100 border-green-600',
  'ghost:green': 'text-green-600 hover:bg-green-100 focus-visible:bg-green-100',

  'solid:yellow':
    'text-white bg-yellow-500 hover:bg-yellow-600 focus-visible:bg-yellow-500 aria-disabled:hover:bg-yellow-500 aria-disabled:hover:border-yellow-500 border-yellow-500 hover:border-yellow-600',
  'outline:yellow': 'text-yellow-600 hover:bg-yellow-100 border-yellow-600',
  'ghost:yellow': 'text-yellow-600 hover:bg-yellow-100 focus-visible:bg-yellow-100',

  'solid:purple':
    'text-white bg-purple-700 hover:bg-purple-800 focus-visible:bg-purple-700 aria-disabled:hover:bg-purple-700 aria-disabled:hover:border-purple-700 border-purple-700 hover:border-purple-800',
  'outline:purple': 'text-purple-700 hover:bg-purple-100 border-purple-700',
  'ghost:purple': 'text-purple-700 hover:bg-purple-100 focus-visible:bg-purple-100',

  'solid:gray':
    'text-white bg-gray-500 hover:bg-gray-600 focus-visible:bg-gray-500 aria-disabled:hover:bg-gray-500 aria-disabled:hover:border-gray-500 border-gray-500 hover:border-gray-600',
  'outline:gray': 'text-gray-600 hover:bg-gray-100 border-gray-600',
  'ghost:gray': 'text-gray-500 hover:bg-gray-100 focus-visible:bg-gray-100',

  'solid:gray_alt':
    'text-black bg-gray-200 hover:bg-gray-300 focus-visible:bg-gray-200 aria-disabled:hover:bg-gray-200 aria-disabled:hover:border-gray-200 border-gray-200 hover:border-gray-300',
  'outline:gray_alt': 'text-gray-600 bg-white hover:bg-gray-100 border-gray-300',
  'ghost:gray_alt': 'text-black hover:bg-gray-100 focus-visible:bg-gray-100',

  'solid:white':
    'text-black bg-white hover:bg-gray-100 focus-visible:bg-white aria-disabled:hover:bg-white border-white hover:border-white',
  'outline:white': 'text-white hover:bg-white/20 border-white',
  'ghost:white': 'text-white hover:bg-white/20 focus-visible:bg-white/20',
};

// Figma (node 652:14) pairs Small/Base buttons with 18px icons and Large with 24px — Zone::Icon
// `lg` (text-lg, 1.8rem) and `xxl` (text-2xl, 2.4rem) respectively. Zone::Button::ICON_SIZE_MAPPINGS
// still sends every size to `lg`; the design is the source of truth here, and its Large symbols
// embed `add/24` where Small/Base embed `add/18`.
const ICON_SIZE_MAPPINGS: Record<ButtonSize, 'lg' | 'xxl'> = {
  sm: 'lg',
  md: 'lg',
  lg: 'xxl',
};

/**
 * Does this slot's output actually paint a label? `$slots.default` merely being present isn't
 * enough — a `v-if` that's false compiles to a Comment, an empty `v-for` to a childless Fragment,
 * and pretty-printed markup leaves whitespace-only Text behind. All three should still count as
 * icon-only.
 */
function rendersContent(nodes: VNode[]): boolean {
  return nodes.some((node) => {
    if (node.type === Comment) {return false;}

    if (node.type === Text) {return String(node.children ?? '').trim() !== '';}

    if (node.type === Fragment) {
      return Array.isArray(node.children) && rendersContent(node.children as VNode[]);
    }

    return true;
  });
}
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneButton', inheritAttrs: false });

const props = defineProps({
  variant: {
    type: String as PropType<ButtonVariant>,
    default: 'solid',
    validator: (v: string) => VARIANTS.includes(v as ButtonVariant),
  },
  color: {
    type: String as PropType<ButtonColor>,
    default: 'primary',
    validator: (v: string) => COLORS.includes(v as ButtonColor),
  },
  size: {
    type: String as PropType<ButtonSize>,
    default: 'md',
    validator: (v: string) => SIZES.includes(v as ButtonSize),
  },
  /**
   * Applies `aria-disabled`, not the native attribute, so the element stays focusable and
   * hoverable, which is what lets a tooltip work on a disabled button. Clicks are still suppressed.
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /** Renders an `<a>` instead of a `<button>`; a disabled `<a>` drops its href. */
  href: {
    type: String,
    default: null,
  },
  /** Only on `<button>`. */
  type: {
    type: String as PropType<'button' | 'submit' | 'reset'>,
    default: 'button',
  },
  /** Zone icon name (see ZoneIcon). */
  leadingIcon: {
    type: String as PropType<IconName>,
    default: null,
  },
  /** Zone icon name (see ZoneIcon). */
  trailingIcon: {
    type: String as PropType<IconName>,
    default: null,
  },
  /**
   * Render a square, zero-horizontal-padding button holding nothing but an icon.
   * Tri-state: `null` (the default) infers it from an empty default slot, `true`/`false` force it.
   */
  iconOnly: {
    type: Boolean as PropType<boolean | null>,
    default: null,
  },
  /**
   * Render a circle instead of a rounded rect — the Zone spec's "Shape: Round" icon button.
   * Square is the default: the spec reserves round for the desktop app and start/stop actions.
   */
  rounded: {
    type: Boolean,
    default: false,
  },
  /**
   * Replace the computed size classes with a custom set, keeping variant/color/shape/focus.
   * The escape hatch ZoneIconButton uses to reach the Zone spec's XS/S icon buttons (16px/20px),
   * which are smaller than any ZoneButton height. Like ICON_ONLY_SIZE_CLASSES this REPLACES the
   * size entry rather than being appended to it, so there's never an `h-8` vs `h-4` pair whose
   * winner depends on Tailwind's output order.
   */
  sizeClass: {
    type: String,
    default: null,
  },
  /** Skip the computed variant/color/size classes entirely, for fully custom styling. */
  reset: {
    type: Boolean,
    default: false,
  },
  /**
   * Hover text (ZoneTooltip). Works on a disabled button. Wrap the button in ZoneTooltip yourself
   * for rich content.
   */
  tooltip: {
    type: String,
    default: null,
  },
});

const slots = defineSlots<{
  /** The label. */
  default?(): any;
  /** Overrides `leadingIcon` with markup. */
  'leading-icon'?(): any;
  /** Overrides `trailingIcon` with markup. */
  'trailing-icon'?(): any;
  /** A ZoneIndicator for the corner badge: you pass it, colour, border, count and all; the button only positions it. */
  indicator?(): any;
}>();
const attrs = useAttrs();

const tag = computed<'a' | 'button'>(() => (props.href ? 'a' : 'button'));

const resolvedHref = computed<string | undefined>(() => {
  if (tag.value !== 'a' || props.disabled) {return undefined;}

  return props.href ?? undefined;
});

const iconSize = computed<'lg' | 'xxl'>(() => ICON_SIZE_MAPPINGS[props.size]);

/**
 * Safe to cache: Vue 3.5 tracks `$slots` as a reactive dep (`updateSlots` triggers it), so
 * this recomputes when the label appears or disappears — the vnode walk below only runs when
 * the slot actually changed.
 */
const isIconOnly = computed<boolean>(() => {
  if (props.iconOnly !== null) {return props.iconOnly;}

  const label = slots.default;

  return !label || !rendersContent(label());
});

/** Mutually exclusive with rounded-md rather than layered over it — see ICON_ONLY_SIZE_CLASSES. */
const shapeClass = computed<string>(() => (props.rounded ? 'rounded-full' : 'rounded-md'));

/**
 * Figma's "Badge slot" is a fixed 8x8 anchor box hung off the corner (`-right-1 -top-1`) whose
 * child is `items-center justify-end` — so an 8px dot lands exactly on the corner, and a wider
 * count badge grows leftwards from the same point while staying centred on it.
 *
 * The percentage translates this replaced (`translate-x-1/2` for a dot, `translate-x-1/3` for
 * a count) were fractions of the badge's OWN width, so the longer the count the further off
 * the button it drifted — 4px for a dot, ~9px for "99+". The anchor box has no such
 * dependency, so the dot/count split — and the `hasIndicatorContent` vnode walk behind it —
 * is gone.
 *
 * Round is the one case Figma doesn't spec: a circle has no corner to straddle, so the anchor
 * tucks 2px in or the badge floats off the arc.
 */
const indicatorWrapperClasses = computed<string>(() => {
  const inset = props.rounded ? '-right-0.5' : '-right-1';

  return `absolute ${inset} -top-1 flex h-2 w-2 items-center justify-end`;
});

const buttonClasses = computed<string>(() => {
  if (props.reset) {return '';}

  return [
    'relative inline-flex items-center justify-center gap-2 font-regular',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700',
    'focus-visible:inset-ring-[1.5px] focus-visible:inset-ring-white',
    'aria-disabled:cursor-not-allowed',
    shapeClass.value,
    props.sizeClass ?? (isIconOnly.value ? ICON_ONLY_SIZE_CLASSES[props.size] : SIZE_CLASSES[props.size]),
    VARIANT_CLASSES[props.variant],
    COMPOUND_CLASSES[`${props.variant}:${props.color}`],
  ].join(' ');
});

const rootAttrs = computed<Record<string, unknown>>(() => {
  const { class: attrClass, onClick, ...restAttrs } = attrs as Record<string, unknown> & { class?: unknown; onClick?: unknown };

  return {
    ...restAttrs,
    type: tag.value === 'button' ? props.type : undefined,
    href: resolvedHref.value,
    'aria-disabled': props.disabled,
    class: [buttonClasses.value, attrClass],
    // aria-disabled keeps the element clickable (so the tooltip still works), so the caller's
    // handler is swapped out for one that only swallows the click.
    onClick: props.disabled ? swallowClick : onClick,
  };
});

function swallowClick(event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
}
</script>
